import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Warning, XCircle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { InvoiceRow, InvoiceLabels } from "./types";

interface Props {
  open: boolean;
  row: InvoiceRow | null;
  labels: InvoiceLabels;
  onClose: () => void;
  onConfirm: (reason: string, explanation?: string) => Promise<void> | void;
}

const REASONS = ["wrong_customer", "wrong_amount", "wrong_service", "customer_request", "other"] as const;

export function InvoiceVoidDialog({ open, row, labels, onClose, onConfirm }: Props) {
  const [reason, setReason] = React.useState<(typeof REASONS)[number]>("wrong_customer");
  const [explanation, setExplanation] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setReason("wrong_customer");
      setExplanation("");
      setBusy(false);
    }
  }, [open]);

  const valid = reason !== "other" || explanation.trim().length >= 10;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onConfirm(reason, reason === "other" ? explanation.trim() : undefined);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-biz-surface-elevated border-biz-border sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle size={20} weight="fill" className="text-biz-danger" />
            {labels.voidDialog.title}
          </DialogTitle>
          <DialogDescription className="text-biz-foreground-muted">
            {row?.invoiceNumber ?? "—"} · {row?.customer.name}
          </DialogDescription>
        </DialogHeader>

        <div
          role="alert"
          className="bg-biz-warning-subtle border border-biz-warning-border rounded-[10px] p-3 flex gap-2 text-biz-warning-foreground text-sm"
        >
          <Warning size={18} weight="fill" className="shrink-0 mt-0.5 text-biz-warning" />
          <span>{labels.voidDialog.description}</span>
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-biz-foreground mb-1.5">
            {labels.voidDialog.reason}
          </legend>
          {REASONS.map((r) => {
            const checked = reason === r;
            return (
              <label
                key={r}
                className={cn(
                  "flex items-center gap-2 px-3 h-10 rounded-biz-btn border text-sm cursor-pointer",
                  checked
                    ? "border-biz-primary bg-biz-primary-subtle text-biz-primary-subtle-fg"
                    : "border-biz-border text-biz-foreground hover:bg-biz-muted",
                )}
              >
                <input
                  type="radio"
                  name="void-reason"
                  value={r}
                  checked={checked}
                  onChange={() => setReason(r)}
                  className="accent-biz-primary"
                />
                {labels.voidDialog.reasons[r]}
              </label>
            );
          })}
        </fieldset>

        {reason === "other" && (
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-biz-foreground">{labels.voidDialog.explanationLabel}</span>
            <textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder={labels.voidDialog.explanationPlaceholder}
              rows={3}
              minLength={10}
              className="px-3 py-2 bg-biz-bg border border-biz-border rounded-biz-btn text-sm biz-focus-ring resize-none"
            />
          </label>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-10 px-4 rounded-biz-btn border border-biz-border text-sm font-semibold hover:bg-biz-muted biz-focus-ring disabled:opacity-50"
          >
            {labels.voidDialog.cancel}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || busy}
            className="h-10 px-4 inline-flex items-center gap-1.5 rounded-biz-btn bg-biz-danger text-biz-danger-foreground text-sm font-semibold hover:opacity-90 biz-focus-ring disabled:opacity-50"
          >
            <XCircle size={14} />
            {labels.voidDialog.confirm}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
